/**
 * Financial computations for the Budget module.
 *
 * Iter 16 — Revenue model rewritten to support a 60-month (5-year) horizon
 * where each stream can be either:
 *   • "one-time"   — single payment at month `start_month`
 *   • "recurring"  — payment every `recurrence` cycle (monthly | quarterly |
 *                    annual) starting at `start_month`, with optional
 *                    `monthly_growth_rate` compounded month over month and an
 *                    optional `duration_months` (0 = unlimited until horizon).
 *
 * Backwards compatibility — legacy streams with `monthly_amount` / `growth_rate`
 * are normalized via `normalizeRevenueStream()` into the new schema.
 *
 * Expenses still follow the simpler model: a flat `fixed_monthly` and
 * `variable_monthly` that apply every month from month 1, with optional
 * yearly compound growth (`yearly_expense_growth`) applied per year.
 */

/**
 * Convert an EXPENSE row from any legacy / partial shape to the canonical
 * schema. Legacy expenses were `{item, amount}` (flat monthly recurring from
 * M1 to infinity) — these get auto-promoted to a normalized recurring stream.
 *
 * Identical shape to revenue streams (so the UI can share a renderer):
 *   { name, type, amount, start_month, recurrence, monthly_growth_rate, duration_months }
 */
export function normalizeExpense(e) {
  if (!e) return null;
  return {
    name: e.name || e.item || "",
    type: e.type === "one-time" ? "one-time" : "recurring",
    amount: e.amount != null ? parseFloat(e.amount) || 0 : 0,
    start_month: Math.max(1, parseInt(e.start_month, 10) || 1),
    recurrence: ["monthly", "quarterly", "annual"].includes(e.recurrence) ? e.recurrence : "monthly",
    monthly_growth_rate: parseFloat(e.monthly_growth_rate) || 0,
    duration_months: parseInt(e.duration_months, 10) || 0,
  };
}

/**
 * Expense contributed by a single expense line in month `m` (1-indexed).
 * Mirrors `revenueForMonth` so callers can iterate over fixed + variable
 * arrays the same way they iterate revenue streams.
 */
export function expenseForMonth(expense, m) {
  const e = normalizeExpense(expense);
  if (!e) return 0;
  if (m < e.start_month) return 0;
  if (e.duration_months > 0 && m >= e.start_month + e.duration_months) return 0;
  const offset = m - e.start_month;
  if (e.type === "one-time") {
    return offset === 0 ? e.amount : 0;
  }
  if (e.recurrence === "quarterly" && offset % 3 !== 0) return 0;
  if (e.recurrence === "annual" && offset % 12 !== 0) return 0;
  return e.amount * Math.pow(1 + e.monthly_growth_rate / 100, offset);
}

/**
 * Year-1 burn = the sum of expenses in month 1 (everything active that month).
 * Used for the overview KPI cards. NOT the same as the yearly total.
 */
export function month1Expense(fixed = [], variable = []) {
  const all = [...fixed, ...variable];
  return all.reduce((s, e) => s + expenseForMonth(e, 1), 0);
}

export const HORIZON_MONTHS = 60; // 5 years

/**
 * Convert a stream from any legacy / partial shape into the canonical schema.
 * Always returns a fresh object — never mutates the input.
 */
export function normalizeRevenueStream(r) {
  if (!r) return null;
  const legacyMonthly = r.monthly_amount != null ? parseFloat(r.monthly_amount) || 0 : null;
  const legacyGrowth = r.growth_rate != null ? parseFloat(r.growth_rate) || 0 : null;
  return {
    name: r.name || "",
    type: r.type === "one-time" ? "one-time" : "recurring",
    amount: r.amount != null ? parseFloat(r.amount) || 0 : (legacyMonthly ?? 0),
    start_month: Math.max(1, parseInt(r.start_month, 10) || 1),
    recurrence: ["monthly", "quarterly", "annual"].includes(r.recurrence) ? r.recurrence : "monthly",
    monthly_growth_rate: r.monthly_growth_rate != null
      ? parseFloat(r.monthly_growth_rate) || 0
      : (legacyGrowth ?? 0),
    duration_months: parseInt(r.duration_months, 10) || 0, // 0 = until horizon
  };
}

/**
 * Return the revenue contributed by a single (normalized) stream in month `m`
 * (1-indexed: m=1 is the first month). Returns 0 if the stream is not yet
 * active or has already ended.
 */
export function revenueForMonth(stream, m) {
  const s = normalizeRevenueStream(stream);
  if (!s) return 0;
  if (m < s.start_month) return 0;
  if (s.duration_months > 0 && m >= s.start_month + s.duration_months) return 0;
  const offset = m - s.start_month;

  if (s.type === "one-time") {
    return offset === 0 ? s.amount : 0;
  }

  // recurring — does THIS month fall on a payment?
  if (s.recurrence === "monthly") {
    // pays every month
  } else if (s.recurrence === "quarterly") {
    if (offset % 3 !== 0) return 0;
  } else if (s.recurrence === "annual") {
    if (offset % 12 !== 0) return 0;
  }
  // Compound growth applied month-over-month, even if not every month pays.
  return s.amount * Math.pow(1 + s.monthly_growth_rate / 100, offset);
}

/**
 * Build the monthly projection for the entire horizon (default 60 months).
 *
 * Two expense shapes are supported (callers can mix):
 *   • `fixed_monthly` and `variable_monthly` arrays of normalized expense lines
 *     (each with its own type/start/recurrence) — preferred.
 *   • `totalMonthlyExpenses` flat number — applied every month, with the
 *     legacy `yearly_expense_growth` compounded per year.
 *
 * Returns an array of `{monthIdx (0-based), monthLabel, yearIdx (0-based),
 *                       revenue, expenses, cashFlow, cumulative}`.
 */
export function computeMonthlyProjection({
  revenue_streams = [],
  fixed_monthly = null,
  variable_monthly = null,
  totalMonthlyExpenses = 0,
  totalStartup = 0,
  yearly_expense_growth = 0,
  horizonMonths = HORIZON_MONTHS,
  monthLabels = [],
}) {
  const useExpenseLines = Array.isArray(fixed_monthly) || Array.isArray(variable_monthly);
  const normalizedFixed = (fixed_monthly || []).map(normalizeExpense);
  const normalizedVariable = (variable_monthly || []).map(normalizeExpense);

  const data = [];
  let cumCash = -totalStartup;
  for (let m = 1; m <= horizonMonths; m++) {
    const yearIdx = Math.floor((m - 1) / 12);
    const revenue = revenue_streams.reduce((s, r) => s + revenueForMonth(r, m), 0);
    let fixedExp = 0;
    let variableExp = 0;
    if (useExpenseLines) {
      fixedExp = normalizedFixed.reduce((s, e) => s + expenseForMonth(e, m), 0);
      variableExp = normalizedVariable.reduce((s, e) => s + expenseForMonth(e, m), 0);
    } else {
      // legacy flat: split is not known, attribute all to fixed
      const flat = totalMonthlyExpenses * Math.pow(1 + (parseFloat(yearly_expense_growth) || 0) / 100, yearIdx);
      fixedExp = flat;
    }
    const expenses = fixedExp + variableExp;
    const cashFlow = revenue - expenses;
    cumCash += cashFlow;
    data.push({
      monthIdx: m - 1,
      monthNumber: m,
      yearIdx,
      monthLabel: monthLabels[(m - 1) % 12] || `M${m}`,
      monthFullLabel: `Y${yearIdx + 1} · ${monthLabels[(m - 1) % 12] || `M${m}`}`,
      revenue: Math.round(revenue),
      fixedExpenses: Math.round(fixedExp),
      variableExpenses: Math.round(variableExp),
      expenses: Math.round(expenses),
      cashFlow: Math.round(cashFlow),
      cumulative: Math.round(cumCash),
    });
  }
  return data;
}

/**
 * Aggregate the monthly projection into yearly buckets. Returns up to
 * 5 rows (one per year). Each row has revenue, expenses, operating, tax, net,
 * cumulative.
 */
export function aggregateYearly(monthlyProjection, { taxRate = 25 } = {}) {
  const byYear = {};
  for (const row of monthlyProjection) {
    const y = row.yearIdx;
    if (!byYear[y]) byYear[y] = { revenue: 0, expenses: 0 };
    byYear[y].revenue += row.revenue;
    byYear[y].expenses += row.expenses;
  }
  const years = Object.keys(byYear)
    .map((y) => parseInt(y, 10))
    .sort((a, b) => a - b);
  let cumulativeNet = 0;
  return years.map((yIdx) => {
    const { revenue, expenses } = byYear[yIdx];
    const operating = revenue - expenses;
    const tax = Math.max(0, operating * (taxRate / 100));
    const net = operating - tax;
    cumulativeNet += net;
    return {
      yearIdx: yIdx,
      year: `Año ${yIdx + 1}`,
      revenue: Math.round(revenue),
      expenses: Math.round(expenses),
      operating: Math.round(operating),
      tax: Math.round(tax),
      net: Math.round(net),
      cumulative: Math.round(cumulativeNet),
    };
  });
}

/**
 * First-year totals from a 60-month projection. If the projection includes
 * per-row `fixedExpenses` / `variableExpenses` (new schema), we sum those.
 * Otherwise we fall back to the legacy signature and use the monthly totals
 * passed by the caller.
 */
export function year1Totals(monthlyProjection, totalFixed = 0, totalVariable = 0) {
  const firstYear = (monthlyProjection || []).filter((row) => (row.yearIdx ?? 0) === 0);
  const revenue = firstYear.reduce((s, x) => s + x.revenue, 0);
  const hasBreakdown = firstYear.length > 0 && firstYear[0].fixedExpenses != null;
  const fixedYearly = hasBreakdown
    ? firstYear.reduce((s, x) => s + (x.fixedExpenses || 0), 0)
    : totalFixed * 12;
  const variableYearly = hasBreakdown
    ? firstYear.reduce((s, x) => s + (x.variableExpenses || 0), 0)
    : totalVariable * 12;
  return { revenue, fixedYearly, variableYearly, expenses: fixedYearly + variableYearly };
}

/** Income Statement (P&L) for a given year's revenue + expenses + tax rate. */
export function computePL({ revenue, fixedExpenses, variableExpenses, taxRate = 25 }) {
  const cogs = variableExpenses;
  const grossProfit = revenue - cogs;
  const grossMarginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
  const opex = fixedExpenses;
  const operatingIncome = grossProfit - opex;
  const ebitda = operatingIncome;
  const tax = Math.max(0, operatingIncome * (taxRate / 100));
  const netIncome = operatingIncome - tax;
  const netMarginPct = revenue > 0 ? (netIncome / revenue) * 100 : 0;
  return { revenue, cogs, grossProfit, grossMarginPct, opex, operatingIncome, ebitda, tax, netIncome, netMarginPct };
}

/** Simplified pro-forma balance sheet at end of Year 1. */
export function computeBalance({ initialCapital, startupCosts, year1Revenue, year1Expenses, year1Tax }) {
  const cashEoy = initialCapital - startupCosts + (year1Revenue - year1Expenses) - year1Tax;
  const fixedAssets = startupCosts;
  const totalAssets = cashEoy + fixedAssets;
  const taxesPayable = 0;
  const loans = 0;
  const totalLiabilities = taxesPayable + loans;
  const paidInCapital = initialCapital;
  const retainedEarnings = year1Revenue - year1Expenses - year1Tax;
  const totalEquity = paidInCapital + retainedEarnings;
  return {
    cashEoy, fixedAssets, totalAssets, taxesPayable, loans, totalLiabilities,
    paidInCapital, retainedEarnings, totalEquity,
    balanceCheck: totalAssets - (totalLiabilities + totalEquity),
  };
}

/**
 * Break-even analysis (unchanged math, but accepts the new 60-month projection).
 * "breakEvenMonth" is the first month index (1-based) where cumulative ≥ 0.
 */
export function computeBreakEven({ unitRevenue, unitVariable, totalFixedMonthly, monthlyProjection }) {
  const contributionMargin = unitRevenue - unitVariable;
  const customersPerMonth = contributionMargin > 0 ? totalFixedMonthly / contributionMargin : null;
  const revenuePerMonth = customersPerMonth ? customersPerMonth * unitRevenue : null;
  const contributionMarginPct = unitRevenue > 0 ? (contributionMargin / unitRevenue) * 100 : 0;
  let breakEvenMonth = null;
  for (const m of (monthlyProjection || [])) {
    if (m.cumulative >= 0) {
      breakEvenMonth = (m.monthIdx ?? 0) + 1;
      break;
    }
  }
  return { contributionMargin, contributionMarginPct, customersPerMonth, revenuePerMonth, breakEvenMonth };
}

/**
 * 5-year projection. Now derived from the actual 60-month projection (which
 * already encodes per-stream type/start/recurrence). Expense growth is
 * applied by month inside the projection itself. The caller still passes
 * `taxRate` (and the legacy `revenueGrowth` / `expenseGrowth` args are
 * tolerated but ignored — kept in the signature to avoid breaking callers).
 */
export function computeFiveYears({ monthlyProjection, taxRate = 25 } = {}) {
  if (!monthlyProjection || !monthlyProjection.length) return [];
  return aggregateYearly(monthlyProjection, { taxRate });
}

/** Best / Base / Worst scenarios for Year-1. */
export function computeScenarios({ y1Revenue, fixedYearly, variableYearly, taxRate = 25, scenarios }) {
  const cases = [
    { key: "worst", label: "Worst", revMult: scenarios.worst_revenue_mult, expMult: scenarios.worst_expense_mult },
    { key: "base",  label: "Base",  revMult: 1, expMult: 1 },
    { key: "best",  label: "Best",  revMult: scenarios.best_revenue_mult, expMult: scenarios.best_expense_mult },
  ];
  return cases.map((c) => {
    const revenue = y1Revenue * c.revMult;
    const fixed = fixedYearly * c.expMult;
    const variable = variableYearly * c.expMult;
    const pl = computePL({ revenue, fixedExpenses: fixed, variableExpenses: variable, taxRate });
    return { ...c, ...pl };
  });
}
